import React from "react";
import { Cpu, Layers, ShieldCheck, Zap } from "lucide-react";

export const Principles: React.FC = () => {
  return (
    <section
      id="principles"
      className="py-20 lg:py-28 bg-canvas border-b border-charcoal/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-300 text-xs font-mono font-medium text-charcoal-700 uppercase tracking-widest">
            Operating Philosophy
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
            The Innovriddhi Standard
          </h2>
          <p className="text-base sm:text-lg text-ink-secondary leading-relaxed">
            Three non-negotiable engineering principles that guide how we build,
            test, and ship software for high-velocity field businesses.
          </p>
        </div>

        {/* 3 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Pillar 1 */}
          <div className="rounded-3xl bg-white p-8 border border-charcoal/10 shadow-sm flex flex-col justify-between hover:border-plum/30 transition-colors group">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-plum/10 text-plum flex items-center justify-center group-hover:scale-105 transition-transform">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold text-ink">
                Field Reality Over Valley Mimicry
              </h3>
              <p className="text-sm text-ink-secondary leading-relaxed">
                Indian distribution runs in wholesale mandis with spotty
                cellular coverage, rugged Android handsets, and high transaction
                velocity. We reject Silicon Valley assumptions: our engines are
                offline-first, battery-efficient, and resilient to sudden
                network drops.
              </p>
            </div>
            <div className="pt-6 mt-6 border-t border-charcoal/5 font-mono text-xs text-plum font-semibold">
              01 · Resilient Edge
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="rounded-3xl bg-white p-8 border border-charcoal/10 shadow-sm flex flex-col justify-between hover:border-plum/30 transition-colors group">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-plum/10 text-plum flex items-center justify-center group-hover:scale-105 transition-transform">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold text-ink">
                Operational Density Over SaaS Bloat
              </h3>
              <p className="text-sm text-ink-secondary leading-relaxed">
                Operators manage distribution between phone calls and
                dispatches—they do not want decorative gradients or vanity
                metric cards. We craft high-signal, dense interfaces designed to
                answer stock, attendance, and route questions in under 30
                seconds.
              </p>
            </div>
            <div className="pt-6 mt-6 border-t border-charcoal/5 font-mono text-xs text-plum font-semibold">
              02 · High-Signal UX
            </div>
          </div>

          {/* Pillar 3 */}
          <div className="rounded-3xl bg-white p-8 border border-charcoal/10 shadow-sm flex flex-col justify-between hover:border-plum/30 transition-colors group">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-plum/10 text-plum flex items-center justify-center group-hover:scale-105 transition-transform">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold text-ink">
                Audit-Grade Traceability
              </h3>
              <p className="text-sm text-ink-secondary leading-relaxed">
                When real stock, trade discounts, and field payouts are at
                stake, ambiguity is unacceptable. We enforce dedicated tenant
                database isolation, verifiable GPS coordinates, and reviewable
                event histories that leadership can confidently stand behind.
              </p>
            </div>
            <div className="pt-6 mt-6 border-t border-charcoal/5 font-mono text-xs text-plum font-semibold">
              03 · True Isolation
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
